import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import SelectionDrawer from '../components/SelectionDrawer';
import { apiUrl } from '../utils/apiBase';

const fetchWithTimeout = (url, options = {}, timeout = 10000) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), timeout))
  ]);
};

const AREA_UNIT_LABELS = {
  sq_m: 'm²',
  sqm: 'm²',
  'm²': 'm²',
  m2: 'm²',
  sq_ft: 'ft²',
  sqft: 'ft²',
  ft2: 'ft²',
  hectare: 'hectares',
  hectares: 'hectares',
  hectars: 'hectares',
  ha: 'hectares',
  acre: 'acres',
  acres: 'acres',
  sq_km: 'km²',
  sqkm: 'km²',
  km2: 'km²',
  sq_mi: 'sq mi',
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const numeric = Number.parseFloat(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatAreaValue = (value, unit) => {
  const numeric = normalizeNumber(value);
  if (numeric === null) {
    return null;
  }

  const normalizedUnit = String(unit || '').trim().toLowerCase();
  const label = AREA_UNIT_LABELS[normalizedUnit] || (normalizedUnit ? normalizedUnit : 'm²');

  const formatter = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: numeric < 10 ? 2 : numeric < 100 ? 1 : 0,
  });

  return `${formatter.format(numeric)} ${label}`;
};

const formatCountLabel = (value, noun) => {
  const numeric = normalizeNumber(value);
  if (numeric === null) return null;
  const rounded = Math.round(numeric);
  if (!Number.isFinite(rounded)) return null;
  const suffix = rounded === 1 ? noun : `${noun}s`;
  return `${rounded} ${suffix}`;
};

const titleize = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const cleaned = value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return null;
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

const getPropertyIdentifier = (property, t) => {
  const candidates = [
    property?.reference_code,
    property?.referenceCode,
    property?.unique_property_id,
    property?.uniquePropertyId,
    property?.unique_id,
    property?.uniqueId,
    property?.listing_id,
    property?.listingId,
    property?.external_id,
    property?.externalId,
    property?.mls_id,
    property?.mlsId,
    property?.code,
    property?.property_code,
    property?.propertyCode,
    property?.local_reference,
    property?.localReference,
  ];

  const match = candidates.find((value) => typeof value === 'string' && value.trim());
  if (match) {
    return match.trim();
  }

  if (Number.isFinite(property?.id)) {
    return t ? t('previewPage.propertyNumber', { id: property.id }) : `Property #${property.id}`;
  }

  return t ? t('previewPage.propertyFallback') : 'Property';
};

const getPropertyTypeLabel = (property, t) => {
  const candidates = [
    property?.property_category,
    property?.propertyCategory,
    property?.category,
    property?.type,
    property?.property_usage,
    property?.propertyUsage,
  ];

  const match = candidates.find((value) => typeof value === 'string' && value.trim());
  if (match) {
    return titleize(match) || match;
  }

  if (typeof property?.title === 'string' && property.title.trim()) {
    return property.title.trim();
  }

  return t ? t('previewPage.propertyFallback') : 'Property';
};

const getLocationLabel = (property, t) => {
  const candidates = [
    property?.city,
    property?.region,
    property?.country,
  ];

  const match = candidates.find((value) => typeof value === 'string' && value.trim());
  if (!match) {
    return t ? t('previewPage.locationSubscribersOnly') : 'Location available to subscribers';
  }

  return match.trim();
};

const formatSaleStatus = (propertyType, t) => {
  if (!propertyType) return t ? t('previewPage.statusUnavailable') : 'Status unavailable';
  const text = propertyType.toString().toLowerCase().trim();
  if (['rent', 'for_rent', 'rental', 'lease'].includes(text)) {
    return t ? t('previewPage.forRent') : 'For rent';
  }
  if (['sale', 'for_sale', 'sell'].includes(text)) {
    return t ? t('previewPage.forSale') : 'For sale';
  }
  return titleize(text) || text || (t ? t('previewPage.statusUnavailable') : 'Status unavailable');
};

const formatRelativeTimeFromNow = (dateInput, { locale, t } = {}) => {
  if (!dateInput) return '—';
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const absSeconds = Math.abs(diffSeconds);

  if (absSeconds < 60) {
    return t ? t('previewPage.time.justNow') : 'just now';
  }

  const intervals = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];

  const formatter = typeof Intl !== 'undefined' && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat(locale || undefined, { numeric: 'auto' })
    : null;

  for (const [unit, secondsInUnit] of intervals) {
    if (absSeconds >= secondsInUnit || unit === 'minute') {
      if (formatter) {
        const value = Math.round(diffSeconds / secondsInUnit);
        return formatter.format(value, unit);
      }

      const value = Math.round(absSeconds / secondsInUnit);
      const suffix = value === 1 ? unit : `${unit}s`;
      // Fallback (rare): keep a simple English-ish format if RelativeTimeFormat is missing.
      return diffSeconds > 0 ? `in ${value} ${suffix}` : `${value} ${suffix} ago`;
    }
  }

  return t ? t('previewPage.time.justNow') : 'just now';
};

const LandSeekerPreviewPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectionBounds, setSelectionBounds] = useState(null);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [mapCenter, setMapCenter] = useState([8.9849, -79.5188]);
  const [mapZoom, setMapZoom] = useState(11);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'land_seeker') {
      navigate('/login', { state: { role: 'land_seeker' } });
      return;
    }

    const loadData = async () => {
      try {
        const allItems = [];
        let page = 1;
        let lastPage = 1;
        const perPage = 2000;
        do {
          const response = await fetchWithTimeout(apiUrl(`/properties?per_page=${perPage}&page=${page}`));
          const data = await response.json();
          if (!data.success) {
            toast.error(t('previewPage.failedToLoadProperties'));
            setLoading(false);
            return;
          }
          const items = data.data || [];
          allItems.push(...items);
          lastPage = data.last_page != null ? data.last_page : 1;
          page += 1;
        } while (page <= lastPage);

        const normalized = allItems.map((property) => {
          const lat = property?.latitude;
          const lng = property?.longitude;
          const latitude = lat !== null && lat !== undefined ? parseFloat(lat) : null;
          const longitude = lng !== null && lng !== undefined ? parseFloat(lng) : null;

          return {
            ...property,
            latitude: Number.isFinite(latitude) ? latitude : null,
            longitude: Number.isFinite(longitude) ? longitude : null,
          };
        });

        setProperties(normalized);
      } catch (error) {
        console.error('Error loading properties:', error);
        toast.error(
          error.message === 'Request timeout'
            ? t('common.requestTimeout')
            : t('previewPage.failedToLoadProperties')
        );
      } finally {
        setLoading(false);
      }
    };

    // Use AuthContext user to avoid extra /auth/me calls (prevents 429 loops).
    const active = !!user?.has_active_subscription;
          setHasActiveSubscription(active);
          if (active) {
      navigate('/search', { replace: true });
    }

    loadData();
  }, [isAuthenticated, user?.role, user?.has_active_subscription, navigate, t]);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }

    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;
        setMapCenter([position.coords.latitude, position.coords.longitude]);
        setMapZoom(12);
      },
      (error) => {
        console.warn('Geolocation unavailable:', error.message);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000,
      }
    );

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectionBounds) {
      setFilteredProperties([]);
      return;
    }

    const bounds = L.latLngBounds(selectionBounds);
    const within = properties.filter((property) => {
      if (!Number.isFinite(property.latitude) || !Number.isFinite(property.longitude)) {
        return false;
      }
      return bounds.contains([property.latitude, property.longitude]);
    });

    setFilteredProperties(within);
  }, [selectionBounds, properties]);

  const mapBounds = useMemo(() => {
    const coords = properties
      .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
      .map((p) => [p.latitude, p.longitude]);

    if (!coords.length) return null;
    return L.latLngBounds(coords);
  }, [properties]);

  const handleSelectionChange = useCallback((bounds) => {
    setSelectionBounds(bounds);
  }, []);

  const handleSubscribe = () => {
    navigate('/subscription');
  };

  const handleViewDetails = (propertyId) => {
    if (hasActiveSubscription) {
      navigate(`/property/${propertyId}`);
    } else {
      navigate('/subscription');
    }
  };

  const formatPrice = useCallback((price) => {
    if (typeof price !== 'number') return '—';
    return new Intl.NumberFormat(i18n.language || undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }, [i18n.language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('previewPage.loadingNearby')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('previewPage.header.title')}</h1>
              <p className="text-gray-600 mt-1">
                {t('previewPage.header.subtitle')}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/preferences')}
                className="bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                {t('previewPage.header.managePreferences')}
              </button>
              <button
                onClick={() => logout()}
                className="bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                🚪 {t('navigation.logout')}
              </button>
              <button
                onClick={handleSubscribe}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('previewPage.header.subscribeCta')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="bg-white border shadow-sm rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('previewPage.map.title')}</h2>
              <p className="text-sm text-gray-600">
                {t('previewPage.map.subtitle')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('previewPage.map.loadedCount', {
                  total: properties.length,
                  onMap: properties.filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude)).length,
                })}
              </p>
            </div>
            {selectionBounds ? (
              <div className="text-sm text-gray-700 bg-blue-50 text-blue-800 px-3 py-2 rounded-md font-medium">
                {t('previewPage.map.inSelection', { count: filteredProperties.length })}
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                {t('previewPage.map.noSelection')}
              </div>
            )}
          </div>
          <div className="h-[65vh]">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              minZoom={8}
              maxZoom={14}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
              doubleClickZoom
              whenCreated={(mapInstance) => {
                if (mapBounds) {
                  try {
                    mapInstance.fitBounds(mapBounds, { padding: [50, 50], maxZoom: 12 });
                  } catch (e) {
                    // ignore fit failures
                  }
                }
              }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <SelectionDrawer selectionBounds={selectionBounds} onSelectionChange={handleSelectionChange} />

              {properties
                .filter((property) => Number.isFinite(property.latitude) && Number.isFinite(property.longitude))
                .map((property) => (
                  <Circle
                    key={property.id}
                    center={[property.latitude, property.longitude]}
                    radius={1500}
                    pathOptions={{
                      color: '#2563eb',
                      weight: 1,
                      fillColor: '#60a5fa',
                      fillOpacity: 0.25,
                    }}
                  />
                ))}
            </MapContainer>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-4">
            {!selectionBounds && (
              <div className="bg-white border rounded-lg p-6 text-center text-gray-600">
                <p className="text-base">
                  {t('previewPage.noSelectionHint')}
                </p>
              </div>
            )}

            {selectionBounds && filteredProperties.length === 0 && (
              <div className="bg-white border rounded-lg p-6 text-center text-gray-600">
                <p className="text-base font-medium text-gray-700">{t('previewPage.emptySelectionTitle')}</p>
                <p className="text-sm text-gray-500 mt-2">{t('previewPage.emptySelectionBody')}</p>
              </div>
            )}

            {filteredProperties.map((property) => {
              const propertyIdentifier = getPropertyIdentifier(property, t);
              const propertyTypeLabel = getPropertyTypeLabel(property, t);
              const locationLabel = getLocationLabel(property, t);
              const landSize = formatAreaValue(property.size, property.size_unit);
              const livingArea = formatAreaValue(
                property.living_area_value
                  ?? property.livingAreaValue
                  ?? property.living_area
                  ?? property.house_size
                  ?? property.home_size,
                property.living_area_unit
                  ?? property.livingAreaUnit
                  ?? property.house_size_unit
                  ?? property.home_size_unit
              );
              const bedroomsLabel = formatCountLabel(
                property.bedrooms
                  ?? property.bedroom_count
                  ?? property.num_bedrooms
                  ?? property.beds,
                'bedroom'
              );
              const bathroomsLabel = formatCountLabel(
                property.bathrooms
                  ?? property.bathroom_count
                  ?? property.num_bathrooms
                  ?? property.baths,
                'bathroom'
              );
              const propertyTypeLabelLower = typeof propertyTypeLabel === 'string' ? propertyTypeLabel.toLowerCase() : '';
              const isLikelyResidential = /(house|apartment|condo|villa|home|residence)/i.test(propertyTypeLabelLower);
              const detailLines = [
                landSize ? t('previewPage.details.landSize', { value: landSize }) : null,
                ...(
                  (isLikelyResidential || livingArea || bedroomsLabel || bathroomsLabel)
                    ? [
                        livingArea ? t('previewPage.details.houseSize', { value: livingArea }) : null,
                        bedroomsLabel ? t('previewPage.details.bedrooms', { value: bedroomsLabel }) : null,
                        bathroomsLabel ? t('previewPage.details.bathrooms', { value: bathroomsLabel }) : null,
                      ]
                    : []
                ),
              ].filter(Boolean);
              const onlineSinceLabel = formatRelativeTimeFromNow(property.created_at, { locale: i18n.language, t });
              const priceLabel = formatPrice(property.price);
              const saleStatusLabel = formatSaleStatus(property.property_type, t);

              return (
                <div
                  key={property.id}
                  className="bg-white border rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <div className="text-xs uppercase tracking-wide text-gray-500">
                          {t('previewPage.card.uniqueId')}
                        </div>
                        <div className="text-base font-semibold text-gray-900">
                          {propertyIdentifier}
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>{propertyTypeLabel}</div>
                          <div>{locationLabel}</div>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="text-xs uppercase tracking-wide text-gray-500">
                          {t('previewPage.card.keyDetails')}
                        </div>
                        {(detailLines.length ? detailLines : [t('previewPage.details.subscribersOnly')]).map(
                          (line, index) => (
                            <div key={`${property.id}-detail-${index}`}>{line}</div>
                          )
                        )}
                      </div>

                      <div className="space-y-4 text-sm text-gray-600 md:text-right">
                        <div>
                          <div className="text-xs uppercase tracking-wide text-gray-500">{t('previewPage.card.onlineSince')}</div>
                          <div className="font-medium text-gray-900">{onlineSinceLabel}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-wide text-gray-500">{t('previewPage.card.price')}</div>
                          <div className="text-xl font-bold text-gray-900">
                            {priceLabel || t('previewPage.priceOnRequest')}
                          </div>
                          <div className="capitalize text-gray-600">
                            {saleStatusLabel}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="text-xs text-gray-500">
                        {t('previewPage.card.privacyNote')}
                      </div>
                      <button
                        onClick={() => handleViewDetails(property.id)}
                        className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                      >
                        {hasActiveSubscription ? t('previewPage.viewDetails') : t('previewPage.subscribeToUnlock')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-1 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">{t('previewPage.cta.title')}</h3>
              <p className="text-blue-700 mb-4 text-sm">
                {t('previewPage.cta.body')}
              </p>
              <button
                onClick={handleSubscribe}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {t('previewPage.cta.button')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandSeekerPreviewPage;
